import { useState, type ReactNode } from 'react';
import { Button, Checkbox, Input, RadioGroup, Select } from '@/components/ui';
import { useBusinessInquiryFiltersStore } from '@/stores/useBusinessInquiryFiltersStore';
import {
  INQUIRY_MALLS,
  INQUIRY_MALL_SHORT,
  INQUIRY_SEARCH_TYPES,
  INQUIRY_SEARCH_TYPE_LABELS,
  INQUIRY_STATUSES,
  INQUIRY_STATUS_LABELS,
  INQUIRY_TYPES,
  INQUIRY_TYPE_LABELS,
  defaultInquiryFilters,
  kstDateDaysAgo,
} from '../lib/businessInquiryLabels';
import type {
  BusinessInquiryFilters,
  InquiryMall,
  InquirySearchType,
  InquiryStatus,
  InquiryType,
} from '../types/businessInquiry';

/**
 * QA-628 BO-01 검색 영역 — [검색]을 눌러야 조회한다. 접수일(기본 최근 30일)·접수 몰·문의 유형(다중)·처리 상태·검색어.
 * 접수 몰·처리 상태는 BE 가 하나만 받으므로 라디오, 문의 유형은 체크(선택 유형이 하나라도 포함된 문의).
 */
export const BusinessInquiryFiltersBar = () => {
  const applyFilters = useBusinessInquiryFiltersStore((s) => s.applyFilters);
  const reset = useBusinessInquiryFiltersStore((s) => s.reset);
  const [draft, setDraft] = useState<BusinessInquiryFilters>(() => useBusinessInquiryFiltersStore.getState().filters);
  const patch = (p: Partial<BusinessInquiryFilters>) => setDraft((d) => ({ ...d, ...p }));

  const quickRange = (days: number | null) =>
    patch(days === null ? { dateFrom: null, dateTo: null } : { dateFrom: kstDateDaysAgo(days), dateTo: kstDateDaysAgo(0) });

  const toggleType = (type: InquiryType, checked: boolean) =>
    patch({
      types: checked
        ? INQUIRY_TYPES.filter((t) => t === type || draft.types.includes(t))
        : draft.types.filter((t) => t !== type),
    });

  const row = (label: ReactNode, children: ReactNode, htmlFor?: string) => (
    <>
      <span className="py-1.5 font-semibold" style={{ color: 'var(--content-strong)' }}>
        {htmlFor ? <label htmlFor={htmlFor}>{label}</label> : label}
      </span>
      <div className="min-w-0">{children}</div>
    </>
  );

  return (
    <form
      aria-label="비즈니스 문의 검색"
      className="flex flex-col gap-3 rounded-lg p-4 text-[13px]"
      style={{ background: 'var(--surface-raised)', border: '1px solid var(--divider-strong)' }}
      onSubmit={(e) => {
        e.preventDefault();
        applyFilters({ ...draft, keyword: draft.keyword.trim() });
      }}
      data-testid="business-inquiry-filters"
    >
      <div className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-x-3 gap-y-2">
        {row(
          '접수일',
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="date"
              value={draft.dateFrom ?? ''}
              max={draft.dateTo ?? undefined}
              onChange={(e) => patch({ dateFrom: e.target.value || null })}
              aria-label="접수일 시작일"
              className="w-40"
            />
            <span aria-hidden="true">~</span>
            <Input
              type="date"
              value={draft.dateTo ?? ''}
              min={draft.dateFrom ?? undefined}
              onChange={(e) => patch({ dateTo: e.target.value || null })}
              aria-label="접수일 종료일"
              className="w-40"
            />
            {(
              [
                ['오늘', 0],
                ['7일', 6],
                ['30일', 29],
                ['전체', null],
              ] as const
            ).map(([label, days]) => (
              <Button key={label} type="button" variant="outline" size="sm" onClick={() => quickRange(days)}>
                {label}
              </Button>
            ))}
          </div>,
        )}
        {row(
          '접수 몰',
          <RadioGroup
            orientation="horizontal"
            value={draft.mallCode ?? ''}
            onValueChange={(v) => patch({ mallCode: v === '' ? null : (v as InquiryMall) })}
            options={[{ value: '', label: '전체' }, ...INQUIRY_MALLS.map((m) => ({ value: m, label: INQUIRY_MALL_SHORT[m] }))]}
            aria-label="접수 몰"
            className="flex-wrap gap-x-4 gap-y-1"
          />,
        )}
        {row(
          '문의 유형',
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1" role="group" aria-label="문의 유형">
            <label className="inline-flex items-center gap-1.5">
              <Checkbox checked={draft.types.length === 0} onCheckedChange={(c) => c === true && patch({ types: [] })} />
              전체
            </label>
            {INQUIRY_TYPES.map((type) => (
              <label key={type} className="inline-flex items-center gap-1.5">
                <Checkbox
                  checked={draft.types.includes(type)}
                  onCheckedChange={(c) => toggleType(type, c === true)}
                  data-testid={`business-inquiry-type-${type}`}
                />
                {INQUIRY_TYPE_LABELS[type]}
              </label>
            ))}
          </div>,
        )}
        {row(
          '처리 상태',
          <RadioGroup
            orientation="horizontal"
            value={draft.status ?? ''}
            onValueChange={(v) => patch({ status: v === '' ? null : (v as InquiryStatus) })}
            options={[{ value: '', label: '전체' }, ...INQUIRY_STATUSES.map((s) => ({ value: s, label: INQUIRY_STATUS_LABELS[s] }))]}
            aria-label="처리 상태"
            className="flex-wrap gap-x-4 gap-y-1"
          />,
        )}
        {row(
          '검색어',
          <div className="flex max-w-[560px] gap-2">
            <Select
              className="w-32 shrink-0"
              value={draft.searchType ?? ''}
              onChange={(e) => patch({ searchType: (e.target.value || null) as InquirySearchType | null })}
              aria-label="검색어 유형"
              data-testid="business-inquiry-search-type"
            >
              <option value="">전체</option>
              {INQUIRY_SEARCH_TYPES.map((st) => (
                <option key={st} value={st}>
                  {INQUIRY_SEARCH_TYPE_LABELS[st]}
                </option>
              ))}
            </Select>
            <Input
              id="business-inquiry-keyword"
              value={draft.keyword}
              onChange={(e) => patch({ keyword: e.target.value })}
              placeholder="검색어 입력"
              className="min-w-0 flex-1"
              maxLength={100}
              data-testid="business-inquiry-keyword"
            />
          </div>,
          'business-inquiry-keyword',
        )}
      </div>
      <div className="flex justify-center gap-2">
        <Button type="submit" variant="primary" size="sm" data-testid="business-inquiry-search">
          검색
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            setDraft(defaultInquiryFilters());
            reset();
          }}
          data-testid="business-inquiry-reset"
        >
          초기화
        </Button>
      </div>
    </form>
  );
};
